import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  appPathAfterRecovery,
  csrfFromFlow,
  firstFlowMessage,
  recoveryStepFromFlow,
  settingsFlowIdFromRedirect,
} from "../auth/kratosFlow.js";

const cardStyle = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-bright)",
  borderRadius: "8px",
  padding: "32px",
  width: "100%",
  maxWidth: "400px",
};

const recoveryFetch = (url, options = {}) =>
  fetch(url, { ...options, credentials: "include", redirect: "manual" });

/** Both flow and code required; partial or empty params → null (normal recovery). */
function recoveryUrlPrefill(searchParams) {
  const flow = searchParams.get("flow")?.trim() ?? "";
  const code = searchParams.get("code")?.trim() ?? "";
  if (!flow || !code) return null;
  return { flow, code };
}

function applyFlowFromServer(data, { setFlow, setStep, setSearchParams, forceStep, keepCodeParam }) {
  if (!data?.id) return;
  setFlow(data);
  setStep(forceStep ?? recoveryStepFromFlow(data));
  const params = { flow: data.id };
  if (keepCodeParam) params.code = keepCodeParam;
  setSearchParams(params, { replace: true });
}

export default function Recovery() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const flowParam = searchParams.get("flow");
  const codeParam = searchParams.get("code");
  const urlPrefill = useMemo(
    () => recoveryUrlPrefill(searchParams),
    [flowParam, codeParam, searchParams],
  );

  const [flow, setFlow] = useState(null);
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(() => urlPrefill?.code ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (urlPrefill?.code) setCode(urlPrefill.code);
  }, [urlPrefill?.code]);

  const loadFlow = useCallback(async () => {
    setError("");
    const flowId = urlPrefill?.flow ?? flowParam?.trim() ?? "";
    const url =
      flowId !== ""
        ? `/api/auth/recovery/init?flow=${encodeURIComponent(flowId)}`
        : "/api/auth/recovery/init";
    const res = await recoveryFetch(url);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data?.error?.message || firstFlowMessage(data) || "Could not start recovery");
      return;
    }
    if (urlPrefill) {
      applyFlowFromServer(data, {
        setFlow,
        setStep,
        setSearchParams,
        forceStep: "code",
        keepCodeParam: urlPrefill.code,
      });
      return;
    }
    applyFlowFromServer(data, { setFlow, setStep, setSearchParams });
  }, [flowParam, urlPrefill, setSearchParams]);

  useEffect(() => {
    loadFlow();
  }, [loadFlow]);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!flow?.id) return;
    setBusy(true);
    setError("");
    try {
      const res = await recoveryFetch("/api/auth/recovery/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flow_id: flow.id,
          csrf_token: csrfFromFlow(flow),
          email: email.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(firstFlowMessage(data) || "Could not send recovery code");
        if (data?.id) applyFlowFromServer(data, { setFlow, setStep, setSearchParams });
        return;
      }
      applyFlowFromServer(data, { setFlow, setStep, setSearchParams });
    } catch {
      setError("Request failed");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!flow?.id) return;
    setBusy(true);
    setError("");
    try {
      const res = await recoveryFetch("/api/auth/recovery/code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flow_id: flow.id,
          csrf_token: csrfFromFlow(flow),
          code: code.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));

      const settingsFlowId = settingsFlowIdFromRedirect(data);
      if (settingsFlowId) {
        navigate(
          `/settings?tab=profile&flow=${encodeURIComponent(settingsFlowId)}`,
          {
            replace: true,
            state: { fromRecovery: true },
          },
        );
        return;
      }

      if (!res.ok) {
        setError(firstFlowMessage(data) || "Invalid or expired code");
        if (data?.id) applyFlowFromServer(data, { setFlow, setStep, setSearchParams });
        return;
      }
      applyFlowFromServer(data, { setFlow, setStep, setSearchParams });
    } catch {
      setError("Request failed");
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!flow?.id) return;
    setBusy(true);
    setError("");
    try {
      const res = await recoveryFetch("/api/auth/recovery/password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flow_id: flow.id,
          csrf_token: csrfFromFlow(flow),
          password,
        }),
      });
      const data = await res.json().catch(() => ({}));

      // Kratos may signal completion via redirect_browser_to (often …/auth/login).
      if (data?.redirect_browser_to) {
        navigate(appPathAfterRecovery(data), { replace: true, state: { recovered: true } });
        return;
      }

      if (res.type === "opaqueredirect" || (res.status >= 300 && res.status < 400)) {
        navigate("/login", { replace: true, state: { recovered: true } });
        return;
      }

      if (!res.ok) {
        setError(firstFlowMessage(data) || "Could not set password");
        if (data?.id) applyFlowFromServer(data, { setFlow, setStep, setSearchParams });
        return;
      }
      navigate("/login", { replace: true, state: { recovered: true } });
    } catch {
      setError("Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: "var(--bg-base)", color: "var(--text-primary)" }}
    >
      <div style={cardStyle}>
        <h1 style={{ fontSize: "18px", fontWeight: 700, margin: "0 0 8px 0" }}>Reset password</h1>
        <p className="text-secondary" style={{ fontSize: "13px", margin: "0 0 20px 0" }}>
          {step === "email" && "Enter your account email. We will send a 6-digit code."}
          {step === "code" && "Enter the code from your email or recovery link."}
          {step === "password" && "Choose a new password (minimum 12 characters)."}
        </p>

        {error ? <p style={{ color: "#f87171", fontSize: "13px", marginBottom: "12px" }}>{error}</p> : null}

        {step === "email" ? (
          <form onSubmit={submitEmail}>
            <input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              className="w-full"
              style={{
                padding: "10px 12px",
                marginBottom: "16px",
                borderRadius: "6px",
                border: "1px solid var(--border-bright)",
                background: "var(--bg-base)",
                color: "var(--text-primary)",
              }}
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full font-semibold"
              style={{
                height: "44px",
                borderRadius: "6px",
                border: "none",
                background: "var(--accent)",
                color: "#0a0f1a",
                cursor: busy ? "wait" : "pointer",
              }}
            >
              Send code
            </button>
          </form>
        ) : null}

        {step === "code" ? (
          <form onSubmit={submitCode}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              placeholder="6-digit code"
              value={code}
              onChange={(ev) => setCode(ev.target.value)}
              className="w-full"
              style={{
                padding: "10px 12px",
                marginBottom: "16px",
                borderRadius: "6px",
                border: "1px solid var(--border-bright)",
                background: "var(--bg-base)",
                color: "var(--text-primary)",
              }}
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full font-semibold"
              style={{
                height: "44px",
                borderRadius: "6px",
                border: "none",
                background: "var(--accent)",
                color: "#0a0f1a",
                cursor: busy ? "wait" : "pointer",
              }}
            >
              Verify code
            </button>
          </form>
        ) : null}

        {step === "password" ? (
          <form onSubmit={submitPassword}>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={12}
              placeholder="New password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              className="w-full"
              style={{
                padding: "10px 12px",
                marginBottom: "16px",
                borderRadius: "6px",
                border: "1px solid var(--border-bright)",
                background: "var(--bg-base)",
                color: "var(--text-primary)",
              }}
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full font-semibold"
              style={{
                height: "44px",
                borderRadius: "6px",
                border: "none",
                background: "var(--accent)",
                color: "#0a0f1a",
                cursor: busy ? "wait" : "pointer",
              }}
            >
              Set password
            </button>
          </form>
        ) : null}

        <p style={{ marginTop: "20px", fontSize: "12px", textAlign: "center" }}>
          <Link to="/login" style={{ color: "var(--accent)", textDecoration: "none" }}>
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
